import { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import { SwalAlert } from '@/hooks/useSweetAlert';
import ModalBase from '@/components/base/ModalBase';
import type { AdminPost, PostType, PostStatus, PollType } from '@/mocks/adminPosts';
import { postTypeLabels, postStatusLabels } from '@/mocks/adminPosts';

interface PostFormModalProps {
  open: boolean;
  post: AdminPost | null;
  onClose: () => void;
  onSave: (data: PostFormData) => void;
  defaultType?: PostType;
}

export interface PostFormData {
  title: string;
  text: string;
  type: PostType;
  imageUrl: string;
  imageFile: File | null;
  videoUrl: string;
  scheduledDate: string;
  status: PostStatus;
  isFeatured: boolean;
  isSponsored: boolean;
  sponsorName: string;
  pollType: PollType;
  pollOptions: { text: string }[];
}

const postTypes: PostType[] = ['texto', 'imagen', 'video', 'encuesta', 'patrocinada'];
const postStatuses: PostStatus[] = ['publicado', 'borrador', 'programado'];

const MAX_FILE_SIZE_MB = 50;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
const MIN_POLL_OPTIONS = 2;
const MAX_POLL_OPTIONS = 10;

export default function PostFormModal({ open, post, onClose, onSave, defaultType }: PostFormModalProps) {
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [type, setType] = useState<PostType>(defaultType || 'texto');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [status, setStatus] = useState<PostStatus>('borrador');
  const [isFeatured, setIsFeatured] = useState(false);
  const [isSponsored, setIsSponsored] = useState(false);
  const [sponsorName, setSponsorName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'simulating' | 'done' | 'error'>('idle');
  const [saving, setSaving] = useState(false);
  const [pollType, setPollType] = useState<PollType>('single');
  const [pollOptions, setPollOptions] = useState<{ id: number; text: string }[]>([]);
  const [nextOptionId, setNextOptionId] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const initialType = defaultType || 'texto';
    if (post) {
      setTitle(post.title);
      setText(post.text);
      setType(post.type);
      setImageUrl(post.imageUrl || '');
      setImageFile(null);
      setImagePreview(post.imageUrl || '');
      setVideoUrl(post.videoUrl || '');
      setScheduledDate(post.scheduledDate || '');
      setStatus(post.status);
      setIsFeatured(post.isFeatured);
      setIsSponsored(post.isSponsored);
      setSponsorName(post.sponsorName || '');
      setUploadStatus('idle');
      setUploadProgress(0);
      setPollType(post.pollData?.pollType || 'single');
      setPollOptions(post.pollData?.options.map((o) => ({ id: o.id, text: o.text })) || []);
      setNextOptionId(post.pollData ? Math.max(...post.pollData.options.map((o) => o.id), 0) + 1 : 1);
    } else {
      setTitle('');
      setText('');
      setType(initialType);
      setImageUrl('');
      setImageFile(null);
      setImagePreview('');
      setVideoUrl('');
      setScheduledDate('');
      setStatus('borrador');
      setIsFeatured(false);
      setIsSponsored(false);
      setSponsorName('');
      setUploadStatus('idle');
      setUploadProgress(0);
      setPollType('single');
      setPollOptions([]);
      setNextOptionId(1);
    }
  }, [post, open, defaultType]);

  const hasTextOrMedia = (): boolean => {
    if (text.trim()) return true;
    const needsMedia = ['imagen', 'patrocinada'].includes(type);
    const needsVideo = type === 'video';
    if (needsMedia && (imagePreview || imageUrl.trim())) return true;
    if (needsVideo && videoUrl.trim()) return true;
    if (type === 'texto' && text.trim()) return true;
    return false;
  };

  const validateFile = (file: File): string | null => {
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      return 'Formato no soportado. Usa JPG, PNG, WebP, GIF o SVG.';
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `El archivo supera los ${MAX_FILE_SIZE_MB} MB.`;
    }
    return null;
  };

  const handleFile = (file: File) => {
    const error = validateFile(file);
    if (error) {
      setUploadStatus('error');
      return;
    }

    setUploadStatus('simulating');
    setUploadProgress(0);

    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 25 + 5;
      if (progress >= 100) {
        progress = 100;
        clearInterval(interval);
        setUploadProgress(100);
        setUploadStatus('done');
      }
      setUploadProgress(Math.min(progress, 100));
    }, 150);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setImagePreview(dataUrl);
      setImageFile(file);
      setImageUrl('');
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
    setUploadStatus('idle');
    setUploadProgress(0);
  };

  // ── Poll helpers ──
  const handleAddPollOption = () => {
    if (pollOptions.length >= MAX_POLL_OPTIONS) return;
    setPollOptions((prev) => [...prev, { id: nextOptionId, text: '' }]);
    setNextOptionId((prev) => prev + 1);
  };

  const handleRemovePollOption = (id: number) => {
    if (pollOptions.length <= MIN_POLL_OPTIONS) return;
    setPollOptions((prev) => prev.filter((o) => o.id !== id));
  };

  const handlePollOptionChange = (id: number, text: string) => {
    setPollOptions((prev) => prev.map((o) => (o.id === id ? { ...o, text } : o)));
  };

  const isPollValid = (): boolean => {
    if (type !== 'encuesta') return true;
    const filled = pollOptions.filter((o) => o.text.trim().length > 0);
    return filled.length >= MIN_POLL_OPTIONS;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasTextOrMedia()) {
      Swal.fire({
        title: 'Falta contenido',
        text: 'Agregá texto, una imagen o un video para continuar.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      return;
    }

    if (status === 'programado' && !scheduledDate) {
      Swal.fire({
        title: 'Fecha requerida',
        text: 'Seleccioná una fecha y hora para programar la publicación.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      return;
    }

    if (!title.trim()) {
      Swal.fire({
        title: 'Título requerido',
        text: 'La publicación necesita un título.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      return;
    }

    if (!isPollValid()) {
      Swal.fire({
        title: 'Faltan opciones',
        text: `La encuesta necesita al menos ${MIN_POLL_OPTIONS} opciones con texto.`,
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      return;
    }

    if (isSponsored && !sponsorName.trim()) {
      Swal.fire({
        title: 'Nombre del sponsor requerido',
        text: 'Ingresá el nombre del sponsor para la publicación patrocinada.',
        icon: 'warning',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#f59e0b',
        customClass: {
          popup: 'rounded-2xl',
          title: 'font-bold',
          confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
          htmlContainer: 'text-sm',
        },
        buttonsStyling: false,
      });
      return;
    }

    setSaving(true);
    onSave({
      title: title.trim(),
      text: text.trim(),
      type,
      imageUrl: imagePreview || imageUrl.trim(),
      imageFile,
      videoUrl: videoUrl.trim(),
      scheduledDate,
      status,
      isFeatured,
      isSponsored,
      sponsorName: sponsorName.trim(),
      pollType,
      pollOptions: pollOptions.filter((o) => o.text.trim().length > 0).map((o) => ({ text: o.text.trim() })),
    });
    setSaving(false);
    await SwalAlert.toast(post ? 'Publicación actualizada.' : 'Publicación creada.', 'success');
  };

  const showImageField = ['imagen', 'patrocinada'].includes(type);
  const showVideoField = type === 'video';

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={post ? 'Editar publicación' : 'Crear publicación'}
      size="xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="post-form"
            disabled={saving}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </span>
            ) : (
              post ? 'Guardar cambios' : 'Crear publicación'
            )}
          </button>
        </>
      }
    >
      <form id="post-form" onSubmit={handleSubmit} className="space-y-5">
        {/* Tipo */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Tipo <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-1.5">
            {postTypes.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setType(t)}
                className={`px-2.5 py-2 rounded-xl text-[11px] font-semibold cursor-pointer transition-all border ${
                  type === t
                    ? 'bg-primary-50 text-primary-700 border-primary-300'
                    : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                }`}
              >
                {postTypeLabels[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Título */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Título <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="Ej: ¡CONCURSO SORPRESA! Ganá entradas para el festival"
            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
          />
        </div>

        {/* Texto */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Texto
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Escribí el contenido de la publicación..."
            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all resize-none"
          ></textarea>
          <p className="text-[10px] text-foreground-400 mt-1 text-right">
            {text.length}/2000
          </p>
        </div>

        {/* ── Sponsor toggle ── */}
        <div className="p-4 rounded-2xl bg-background-100 border border-background-200 space-y-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsSponsored(!isSponsored)}
              className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all shrink-0 ${
                isSponsored ? 'bg-primary-500 border-primary-500' : 'bg-background-50 border-background-300'
              }`}
            >
              {isSponsored && <i className="ri-check-line text-background-50 text-[10px] font-bold"></i>}
            </button>
            <div>
              <label className="text-sm text-foreground-700 font-medium cursor-pointer" onClick={() => setIsSponsored(!isSponsored)}>
                Publicación patrocinada
              </label>
              <p className="text-[10px] text-foreground-400">Marcá esta opción si la publicación es contenido patrocinado por una marca.</p>
            </div>
          </div>

          {isSponsored && (
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                Nombre del sponsor <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={sponsorName}
                onChange={(e) => setSponsorName(e.target.value)}
                maxLength={80}
                placeholder="Ej: Cerveza Imperial"
                className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
              />
            </div>
          )}
        </div>

        {/* ── Encuesta Options ── */}
        {type === 'encuesta' && (
          <div className="space-y-4 p-4 rounded-2xl bg-amber-50/60 border border-amber-200">
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                Tipo de encuesta
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPollType('single')}
                  className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-all border ${
                    pollType === 'single'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-amber-300'
                  }`}
                >
                  <i className="ri-checkbox-circle-line mr-1.5"></i>
                  Una respuesta
                </button>
                <button
                  type="button"
                  onClick={() => setPollType('multiple')}
                  className={`flex-1 px-3 py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-all border ${
                    pollType === 'multiple'
                      ? 'bg-amber-500 text-white border-amber-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-amber-300'
                  }`}
                >
                  <i className="ri-checkbox-multiple-line mr-1.5"></i>
                  Varias respuestas
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                Opciones <span className="text-red-500">*</span>
                <span className="text-foreground-400 font-normal ml-1">
                  (mín. {MIN_POLL_OPTIONS}, máx. {MAX_POLL_OPTIONS})
                </span>
              </label>
              <div className="space-y-2">
                {pollOptions.map((option, idx) => (
                  <div key={option.id} className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[11px] font-bold shrink-0">
                      {idx + 1}
                    </span>
                    <input
                      type="text"
                      value={option.text}
                      onChange={(e) => handlePollOptionChange(option.id, e.target.value)}
                      maxLength={80}
                      placeholder={`Opción ${idx + 1}...`}
                      className="flex-1 px-3 py-2 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-amber-400/60 focus:border-amber-400 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePollOption(option.id)}
                      disabled={pollOptions.length <= MIN_POLL_OPTIONS}
                      className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
                      title="Quitar opción"
                    >
                      <i className="ri-close-line text-base"></i>
                    </button>
                  </div>
                ))}
              </div>
              {pollOptions.length < MAX_POLL_OPTIONS && (
                <button
                  type="button"
                  onClick={handleAddPollOption}
                  className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-dashed border-amber-300 text-amber-600 text-xs font-semibold hover:bg-amber-50 cursor-pointer transition-colors"
                >
                  <i className="ri-add-line text-sm"></i>
                  Agregar opción
                </button>
              )}
              {pollOptions.length >= MAX_POLL_OPTIONS && (
                <p className="mt-2 text-[10px] text-foreground-400 text-center">
                  Máximo {MAX_POLL_OPTIONS} opciones alcanzado
                </p>
              )}
            </div>
          </div>
        )}

        {/* Imagen — Upload Zone */}
        {showImageField && (
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
              Imagen
            </label>
            {!imagePreview ? (
              <>
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all group ${
                    isDragging
                      ? 'border-primary-400 bg-primary-50/60 scale-[1.01]'
                      : 'border-background-300 hover:border-primary-300 hover:bg-background-100'
                  }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  {uploadStatus === 'simulating' ? (
                    <div className="space-y-3">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
                        <i className="ri-upload-cloud-2-line text-2xl text-primary-500 animate-bounce"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-800">Subiendo imagen...</p>
                        <p className="text-[11px] text-foreground-400 mt-0.5">{Math.round(uploadProgress)}%</p>
                      </div>
                      <div className="w-full max-w-[200px] mx-auto h-1.5 rounded-full bg-background-200 overflow-hidden">
                        <div className="h-full rounded-full bg-primary-500 transition-all duration-200" style={{ width: `${uploadProgress}%` }}></div>
                      </div>
                    </div>
                  ) : uploadStatus === 'error' ? (
                    <div className="space-y-2">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-red-100 flex items-center justify-center">
                        <i className="ri-error-warning-line text-2xl text-red-500"></i>
                      </div>
                      <p className="text-sm font-semibold text-red-600">Formato o tamaño no válido</p>
                      <p className="text-[11px] text-foreground-400">JPG, PNG, WebP, GIF o SVG — máximo {MAX_FILE_SIZE_MB} MB</p>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setUploadStatus('idle'); }} className="text-[11px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer">Intentar de nuevo</button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center group-hover:bg-primary-100 group-hover:scale-110 transition-all duration-200">
                        <i className="ri-image-add-line text-2xl text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-700">Arrastrá una imagen o hacé clic para seleccionar</p>
                        <p className="text-[11px] text-foreground-400 mt-0.5">JPG, PNG, WebP, GIF, SVG — máximo {MAX_FILE_SIZE_MB} MB</p>
                      </div>
                    </div>
                  )}
                </div>
                {imageUrl && (
                  <div className="mt-2 w-full h-32 rounded-xl overflow-hidden bg-background-200">
                    <img src={imageUrl} alt="Preview" className="w-full h-full object-cover object-top" />
                  </div>
                )}
              </>
            ) : (
              <div className="relative rounded-xl overflow-hidden border border-background-200 bg-background-100">
                <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover object-top" />
                <div className="absolute top-2 right-2 flex items-center gap-1.5">
                  <button type="button" onClick={handleRemoveImage} className="w-8 h-8 rounded-lg bg-foreground-950/60 hover:bg-red-600 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm group/rm" title="Quitar imagen">
                    <i className="ri-delete-bin-line text-sm text-background-50 group-hover/rm:scale-110 transition-transform"></i>
                  </button>
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="w-8 h-8 rounded-lg bg-foreground-950/60 hover:bg-foreground-950/80 flex items-center justify-center cursor-pointer transition-colors backdrop-blur-sm" title="Cambiar imagen">
                    <i className="ri-image-edit-line text-sm text-background-50"></i>
                  </button>
                </div>
                <div className="px-3 py-2 flex items-center gap-2 bg-background-50">
                  <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center shrink-0">
                    <i className="ri-check-line text-primary-600 text-xs font-bold"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-foreground-800 truncate">{imageFile ? imageFile.name : 'Imagen cargada'}</p>
                    <p className="text-[10px] text-foreground-400">{imageFile ? `${(imageFile.size / (1024 * 1024)).toFixed(1)} MB — Lista para subir` : 'Lista para publicar'}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold whitespace-nowrap">{imageFile ? 'Local' : 'URL'}</span>
                </div>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" onChange={handleFileInput} className="hidden" />
              </div>
            )}
          </div>
        )}

        {/* Video */}
        {showVideoField && (
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
              URL de Video
            </label>
            <input
              type="url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all"
            />
          </div>
        )}

        {/* Fecha programada */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Fecha programada
            {status === 'programado' && <span className="text-red-500 ml-0.5">*</span>}
          </label>
          <input
            type="datetime-local"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400 transition-all ${
              status === 'programado' && !scheduledDate
                ? 'border-amber-300 bg-amber-50/50'
                : 'border-background-200 bg-background-50'
            }`}
          />
          {status === 'programado' && !scheduledDate && (
            <p className="text-[10px] text-amber-600 mt-1 font-medium">
              Fecha requerida si es programada
            </p>
          )}
        </div>

        {/* Estado */}
        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
            Estado <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            {postStatuses.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setStatus(s)}
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer transition-all border ${
                  status === s
                    ? 'bg-primary-50 text-primary-700 border-primary-300'
                    : 'bg-background-50 text-foreground-500 border-background-200 hover:border-background-300'
                }`}
              >
                {postStatusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Destacada */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setIsFeatured(!isFeatured)}
            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all shrink-0 ${
              isFeatured ? 'bg-amber-500 border-amber-500' : 'bg-background-50 border-background-300'
            }`}
          >
            {isFeatured && <i className="ri-check-line text-background-50 text-[10px] font-bold"></i>}
          </button>
          <label className="text-sm text-foreground-700 font-medium cursor-pointer" onClick={() => setIsFeatured(!isFeatured)}>
            Marcar como publicación destacada
          </label>
        </div>
      </form>
    </ModalBase>
  );
}